import { ApiError } from "@/lib/api";
import type { Transaction, TransactionInput } from "@/lib/types";

// Offline quick add (#42): with no signal, a new movement waits in IndexedDB and is sent when
// the connection comes back. Each one carries its `client_key`, so sending it again (the first
// answer got lost, two tabs flush at once) still saves it once on the server.

export interface QueuedAdd {
  /** the movement's `client_key`, also its key in the store */
  key: string;
  input: TransactionInput & { client_key: string };
  queuedAt: string;
}

export interface QueueStore {
  all(): Promise<QueuedAdd[]>;
  put(item: QueuedAdd): Promise<void>;
  remove(key: string): Promise<void>;
}

const DB_NAME = "savingapp";
const STORE = "pending-adds";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "key" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB"));
  });
}

async function run<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>) {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = op(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("IndexedDB"));
    });
  } finally {
    db.close();
  }
}

/** The browser's IndexedDB; outlives a closed app or a reload. */
export const idbStore: QueueStore = {
  all: async () => {
    if (!("indexedDB" in globalThis)) return [];
    const items = await run("readonly", (s) => s.getAll() as IDBRequest<QueuedAdd[]>);
    return items.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  },
  put: (item) => run("readwrite", (s) => s.put(item)).then(() => undefined),
  remove: (key) => run("readwrite", (s) => s.delete(key)).then(() => undefined),
};

/** A new idempotency key for one quick add. */
export const newClientKey = (): string => crypto.randomUUID();

/** `fetch` itself failed: no signal, or the connection dropped before an answer came back. */
export const isNetworkError = (err: unknown): boolean => err instanceof TypeError;

export function queueAdd(
  input: QueuedAdd["input"],
  store: QueueStore = idbStore,
  now = new Date(),
): Promise<void> {
  return store.put({ key: input.client_key, input, queuedAt: now.toISOString() });
}

export interface FlushResult {
  saved: Transaction[];
  /** refused by the server (e.g. the account was deleted meanwhile): dropped from the queue */
  rejected: { input: TransactionInput; message: string }[];
  /** still waiting (no signal, server down, signed out) */
  left: number;
}

/** The server can't take it right now; try again later instead of dropping it. */
const retryLater = (err: unknown) =>
  isNetworkError(err) ||
  (err instanceof ApiError && (err.status >= 500 || [401, 408, 429].includes(err.status)));

let running: Promise<FlushResult> | null = null;

/**
 * Send every waiting movement, oldest first. Stops at the first one the server can't take yet;
 * one call at a time per page (a second call while one runs gets the same answer).
 */
export function flushQueue(
  post: (input: QueuedAdd["input"]) => Promise<Transaction>,
  store: QueueStore = idbStore,
): Promise<FlushResult> {
  running ??= (async () => {
    const result: FlushResult = { saved: [], rejected: [], left: 0 };
    const items = await store.all();
    for (const [i, item] of items.entries()) {
      try {
        result.saved.push(await post(item.input));
      } catch (err) {
        if (retryLater(err)) {
          result.left = items.length - i;
          break;
        }
        const message = err instanceof Error ? err.message : "Algo salió mal";
        result.rejected.push({ input: item.input, message });
      }
      await store.remove(item.key);
    }
    return result;
  })().finally(() => {
    running = null;
  });
  return running;
}
